import {
  defaultCardPositionConf,
  displayOnlyProduct,
  displayOnlyRelationProduct,
  isAlreadyDoneError,
  ListItem,
  resolveZzimErrorMessage,
} from 'common/constants/'
import {
  arrangeCompositeList,
  CompositeDataMap,
  fetchCompositeListData,
  fetchRelationProductAtFirst,
  mergeListSearchParam,
} from 'common/services/list'
import {
  DEFAULT_PAGE_REL_PRODUCT_SIZE,
  DEFAULT_PAGE_SIZE,
  ListCommonParam,
  ListSearchField,
} from 'common/types/params/list'
import history from 'common/history'

import { StateKey } from 'common/utils/flipState'
import HttpStatus from 'common/utils/httpStatus'
import { Error } from 'common/types/entities/error'
import { setProductZzim, setRelationProductZzim } from 'common/services/zzim'
import { ArrayUtils } from '@shopping/common'
import { RelationProduct } from 'common/types/entities/mongo/relationProduct'
import { CompositeItemList, CompositeItemState } from 'common/types/entities/list'

export interface CompositeParamState {
  type: ListItem
  param: ListCommonParam
  hasMore: boolean
  usable: boolean
}

export interface CompositeListState {
  layout: any
  compositeItems: CompositeItemState[]
  compositeParams: CompositeParamState[]
  page: number
  hasMore: boolean
  itemCount: number
  restoredByHistory: boolean
  fetchListFunc: FetchCompositeListFunc
  listParam: ListCommonParam
  otherOptions?: any
}

const initialState: CompositeListState = {
  layout: null,
  compositeItems: [],
  compositeParams: [],
  page: 0,
  hasMore: true,
  itemCount: 0,
  restoredByHistory: false,
  fetchListFunc: null,
  listParam: null,
}

export interface FetchCompositeListFunc {
  (param: ListCommonParam, otherOptions?: any): Promise<CompositeItemList>
}

const FETCH_COMPOSITE_PRODUCT = 'list/compositeList/FETCH_COMPOSITE_PRODUCT'
const SLICE_COMPOSITE_PRODUCT = 'list/compositeList/SLICE_COMPOSITE_PRODUCT'
const INIT_COMPOSITE_PARAMS = 'list/compositeList/INIT_COMPOSITE_PARAMS'
const SET_ZZIM = 'list/compositeList/SET_ZZIM'

export const initCompositeParams = (
  curState: StateKey,
  compositeParams: CompositeParamState[],
  fetchListFunc?: FetchCompositeListFunc,
  otherOptions?: any,
  listParam?: ListCommonParam,
) => dispatch => {
  dispatch({
    curState,
    compositeParams,
    fetchListFunc,
    otherOptions,
    listParam,
    type: INIT_COMPOSITE_PARAMS,
  })
}

const restoreInfiniteForHistory = (dispatch, curState, fetchListFunc?) => {
  if (!curState.restoredByHistory && history.location && history.location.state && history.location.state.infinite) {
    const state = {
      ...history.location.state.infinite,
      restoredByHistory: true,
    }

    if (fetchListFunc) {
      state.fetchListFunc = fetchListFunc
    }

    dispatch({
      curState,
      state,
      type: FETCH_COMPOSITE_PRODUCT,
    })
    return true
  }

  return false
}

export const fetchCompositeList = (curState: StateKey, listSearchField: ListSearchField, action?: string) => {
  // Use this in Sort and filter
  const mergeParamFn = (storedListParams: CompositeParamState[]): CompositeParamState[] =>
    mergeListSearchParam(storedListParams, listSearchField)
  return fetchCompositeListWithOptions(curState, mergeParamFn, true, 1, false, action)
}

export const fetchCompositeListWithOptions = (
  curState: StateKey,
  paramMakingFn: (storedListParams?: CompositeParamState[]) => CompositeParamState[],
  isAppend: boolean,
  inquiryPage: number,
  isFirstFetch?: boolean,
  action?: string,
) => async (dispatch, getState) => {
  if (isFirstFetch && (action === 'POP' || action === undefined)) {
    if (restoreInfiniteForHistory(dispatch, curState)) {
      return
    }
  }

  const compositeList = getState().compositeList
  const { compositeParams, fetchListFunc, listParam } = compositeList
  let compositeParamStates = null

  try {
    compositeParamStates = paramMakingFn(compositeParams)
  } catch (e) {
    if (!!fetchListFunc) {
      // make listParam from legacy
      // todo: Please refactoring this
      const mockParam = {
        param: listParam,
      } as CompositeParamState
      const params = paramMakingFn([mockParam])

      dispatch(
        fetchCompositeListWrapper(
          fetchListFunc,
          params[0].param,
          curState,
          isAppend,
          inquiryPage,
          isFirstFetch,
          action,
        ),
      )
      return
    }
    throw e
  }

  const mergedCompositeParams = applyFilter(compositeParamStates)

  const baseCardIndex = mergedCompositeParams.findIndex(param => param.usable)
  const baseCardParam = mergedCompositeParams[baseCardIndex]
  const baseCardType = baseCardParam && baseCardParam.type

  const ignoreHasMore = inquiryPage === 1 || !isAppend

  const [fetchedCompositeDataMap, firstRelationProduct]: [CompositeDataMap, CompositeItemState] = await Promise.all([
    fetchCompositeListData(
      mergedCompositeParams,
      inquiryPage,
      ignoreHasMore,
      defaultCardPositionConf,
      getState().subVertical,
    ),
    fetchRelationProductAtFirst(inquiryPage, mergedCompositeParams),
  ])

  const arrangedCompositeList = arrangeCompositeList(
    mergedCompositeParams,
    fetchedCompositeDataMap,
    baseCardType,
    defaultCardPositionConf,
  )

  const nonDuplicatedItems = filterDuplicatedBaseItems(
    arrangedCompositeList,
    compositeList.compositeItems,
    baseCardType,
    inquiryPage,
  )

  const mergedCompositeItems = getMergedCompositeItems(isAppend, inquiryPage, nonDuplicatedItems, compositeList)

  const compositeItems = addFirstRelationProductOnCondition(firstRelationProduct, inquiryPage, mergedCompositeItems)

  const updatedParams = mergedCompositeParams.map(param => {
    const fetchedParam = fetchedCompositeDataMap[param.type]
    return fetchedParam ? fetchedParam.param : param
  })
  const baseCardData = fetchedCompositeDataMap[baseCardType]
  const state = {
    compositeItems,
    compositeParams: updatedParams,
    layout: null,
    page: updatedParams[baseCardIndex].param.page,
    hasMore: updatedParams[baseCardIndex].hasMore,
    itemCount: (baseCardData && baseCardData.totalCount) || compositeList.itemCount,
    restoredByHistory: false,
    fetchListFunc: null,
    otherOptions: null,
  }

  dispatch({
    curState,
    state,
    type: FETCH_COMPOSITE_PRODUCT,
  })
}

export const fetchListByWrapper = (
  curState: StateKey,
  isAppend: boolean,
  inquiryPage: number,
  isFirstFetch?: boolean,
  action?: string,
  otherOptions?: any,
) => async (dispatch, getState) => {
  const { fetchListFunc, listParam } = getState().compositeList

  dispatch(
    fetchCompositeListWrapper(
      fetchListFunc,
      listParam,
      curState,
      isAppend,
      inquiryPage,
      isFirstFetch,
      action,
      otherOptions,
    ),
  )
}

export const fetchCompositeListWrapper = (
  fetchListFunc: FetchCompositeListFunc,
  listParam: ListCommonParam,
  curState: StateKey,
  isAppend: boolean,
  inquiryPage: number,
  isFirstFetch?: boolean,
  action?: string,
  otherOptions?: any,
) => async (dispatch, getState) => {
  if (isFirstFetch && (action === 'POP' || action === undefined)) {
    if (restoreInfiniteForHistory(dispatch, curState, fetchListFunc)) {
      return
    }
  }

  listParam.page = inquiryPage

  const compositeList = getState().compositeList

  const mergedOtherOptions =
    (compositeList.otherOptions && {
      ...compositeList.otherOptions,
      ...otherOptions,
    }) ||
    otherOptions

  const results = await fetchListFunc(listParam, mergedOtherOptions)

  const nonDuplicatedItems = filterDuplicatedBaseItems(
    results.items,
    compositeList.compositeItems,
    results.items[0] && results.items[0].type,
    inquiryPage,
  )
  const mergedCompositeItems = getMergedCompositeItems(isAppend, inquiryPage, nonDuplicatedItems, compositeList)

  const state = {
    fetchListFunc,
    listParam,
    otherOptions: mergedOtherOptions,
    compositeItems: mergedCompositeItems,
    layout: null,
    page: results.param.page,
    hasMore: results.hasMore,
    itemCount: results.itemCount || compositeList.itemCount,
    restoredByHistory: false,
  }

  dispatch({
    curState,
    state,
    type: FETCH_COMPOSITE_PRODUCT,
  })
}

const applyFilter = (paramStates: CompositeParamState[]): CompositeParamState[] => {
  if (paramStates && paramStates.length > 0 && !paramStates[0].param.filter) return paramStates

  return paramStates.map(paramState => ({
    ...paramState,
    usable: adjustUsable(paramState.type, paramState.param.filter),
    param: {
      ...paramState.param,
      pageSize: adjustPageCount(paramState.type, paramState.param.filter, paramState.param.pageSize),
    },
  }))
}

const adjustPageCount = (type, filter, originalCount) => {
  if (type === ListItem.RELATION_PRODUCT) {
    if (displayOnlyRelationProduct(filter)) return DEFAULT_PAGE_SIZE

    return DEFAULT_PAGE_REL_PRODUCT_SIZE
  }

  return originalCount
}

const adjustUsable = (type, filter) => {
  if (displayOnlyRelationProduct(filter) && type === ListItem.WINDOWPRODUCT) return false

  if (displayOnlyProduct(filter) && type === ListItem.RELATION_PRODUCT) return false

  return true
}

const getMergedCompositeItems = (isAppend: boolean, inquiryPage: number, nonDuplicatedItems, compositeList) => {
  if (isAppend) {
    return inquiryPage === 1 ? nonDuplicatedItems : [...(compositeList.compositeItems || []), ...nonDuplicatedItems]
  }
  return [...nonDuplicatedItems, ...(compositeList.compositeItems || [])]
}

function addFirstRelationProductOnCondition(
  firstRelationProduct: CompositeItemState,
  inquiryPage: number,
  mergedCompositeItems,
) {
  return firstRelationProduct && inquiryPage === 1
    ? [firstRelationProduct, ...mergedCompositeItems]
    : mergedCompositeItems
}

export const storeSlicedCompositeList = (curState: StateKey, path, startKey, endKey, infiniteStatus) => async (
  dispatch,
  getState,
) => {
  const { compositeList } = getState()
  const currentCompositeList: CompositeListState = compositeList

  if (!currentCompositeList) return
  const sliceList = currentCompositeList.compositeItems.filter(item => {
    if (item.groupKey >= startKey && item.groupKey <= endKey) {
      return item
    }
  })

  // Push state can't be clone func
  const { fetchListFunc } = currentCompositeList

  const state = {
    ...currentCompositeList,
    layout: infiniteStatus,
    compositeItems: sliceList,
    restoredByHistory: false,
    fetchListFunc: null,
  }

  history.replace(history.location.pathname + history.location.search + history.location.hash, { infinite: state })

  dispatch({
    curState,
    state,
    fetchListFunc,
    type: SLICE_COMPOSITE_PRODUCT,
  })
}

interface ZzimParams {
  curState: StateKey
  productId: string
  zzimed: boolean
  listItem: ListItem
  comparedId?: string
}

export const toggleZzim = (params: ZzimParams) => async (dispatch, getState) => {
  if (!params) return
  const { curState, productId, zzimed, listItem, comparedId } = params
  let responseData
  if ([ListItem.RELATION_PRODUCT, ListItem.COORDI_PRODUCT].includes(listItem)) {
    responseData = await setRelationProductZzim(productId, zzimed)
  } else {
    responseData = await setProductZzim(productId, zzimed)
  }
  if (!responseData.bSuccess) {
    const error: Error = {
      status: responseData.sErrorCode === 'ACCESS_DENIED' ? HttpStatus.UNAUTHORIZED : HttpStatus.OK,
      message: resolveZzimErrorMessage(
        responseData.htReturnValue.sErrorCode,
        zzimed,
        responseData.htReturnValue.sMessage,
      ),
    }
    if (isAlreadyDoneError(responseData.htReturnValue.sErrorCode)) {
      dispatch({
        curState,
        productId,
        listItem,
        comparedId,
        type: SET_ZZIM,
      })
    }
    throw error
  }

  dispatch({
    curState,
    productId,
    listItem,
    comparedId,
    type: SET_ZZIM,
  })
}

const findProductItemIndex = (compositeItems: CompositeItemState[], productId: string, listItem: ListItem) => {
  return (
    compositeItems &&
    compositeItems.findIndex(compositeItem => compositeItem.type === listItem && compositeItem.item._id === productId)
  )
}

const getZzimToggledProductItem = (compositeItem: CompositeItemState) => {
  return {
    ...compositeItem,
    item: {
      ...compositeItem.item,
      zzimCount: compositeItem.item.isMyZzim ? compositeItem.item.zzimCount - 1 : compositeItem.item.zzimCount + 1,
      isMyZzim: !compositeItem.item.isMyZzim,
    },
  }
}

const getZzimToggledCoordiProductItem = (compositeItem: CompositeItemState, relationProductId: string) => {
  if (!compositeItem || !relationProductId) return

  const targetItemIndex =
    compositeItem.item &&
    ArrayUtils.isNotEmpty(compositeItem.item.relationProducts) &&
    compositeItem.item.relationProducts.findIndex(rel => rel._id === relationProductId)
  const targetItem: RelationProduct = targetItemIndex > -1 && compositeItem.item.relationProducts[targetItemIndex]
  if (!targetItem) {
    return compositeItem
  }

  targetItem.zzimCount = targetItem.isMyZzim ? targetItem.zzimCount - 1 : targetItem.zzimCount + 1
  targetItem.isMyZzim = !targetItem.isMyZzim

  return {
    ...compositeItem,
    item: {
      ...compositeItem.item,
      relationProducts: [
        ...compositeItem.item.relationProducts.slice(0, targetItemIndex),
        targetItem,
        ...compositeItem.item.relationProducts.slice(targetItemIndex + 1),
      ],
    },
  }
}

const filterDuplicatedBaseItems = (
  target: CompositeItemState[] = [],
  source: CompositeItemState[] = [],
  baseCardType: ListItem,
  inquiryPage?: number,
) => {
  if (inquiryPage === 1) return target
  const baseCardIds = source.reduce((ids, sourceItem) => {
    sourceItem.type === baseCardType && ids.add(sourceItem.id)
    return ids
  }, new Set())
  return target.filter(targetItem => targetItem.type !== baseCardType || !baseCardIds.has(targetItem.id))
}

const compositeListReducer = (state = initialState, action) => {
  switch (action.type) {
    case INIT_COMPOSITE_PARAMS:
      return {
        ...state,
        ...action,
      }
    case FETCH_COMPOSITE_PRODUCT:
    case SLICE_COMPOSITE_PRODUCT:
      return action.state
    case SET_ZZIM:
      const productId = action.comparedId || action.productId
      const targetItemIndex = findProductItemIndex(state.compositeItems, productId, action.listItem)
      const targetItem = state.compositeItems[targetItemIndex]
      const item =
        action.listItem === ListItem.COORDI_PRODUCT
          ? getZzimToggledCoordiProductItem(targetItem, action.productId)
          : getZzimToggledProductItem(targetItem)
      return {
        ...state,
        compositeItems: [
          ...state.compositeItems.slice(0, targetItemIndex),
          item,
          ...state.compositeItems.slice(targetItemIndex + 1),
        ],
      }
    default:
      return state
  }
}

export default compositeListReducer
